import { useEffect, useRef, useState } from 'react';
import { cancelPracticesListRequestsFor, getPracticesList } from '../services/practices/getPractices';
import { Practice } from '../types';

// Module-level cache: practiceId → Practice (matches useCustomFields pattern)
const practiceCache = new Map<string, Practice>();
const inFlightRequests = new Map<string, Promise<Practice[]>>();

// Per-ID generation counter. Incremented by invalidatePracticeCache so that
// in-flight responses that started before the invalidation do not repopulate
// the cache with stale data.
const idGeneration = new Map<string, number>();

export const invalidatePracticeCache = (ids: string[]) => {
  ids.forEach((id) => {
    practiceCache.delete(id);
    idGeneration.set(id, (idGeneration.get(id) ?? 0) + 1);
  });
  // Drop hook-level in-flight batches that overlap the invalidated IDs.
  for (const key of [...inFlightRequests.keys()]) {
    if (ids.some((id) => key.split(',').includes(id))) {
      inFlightRequests.delete(key);
    }
  }
  // Drop the service-level pending request for the same IDs so the next
  // mount cannot receive the stale response via service deduplication.
  cancelPracticesListRequestsFor(ids);
};

export const useBatchedPractices = (
  practiceIds: string[],
): {
  practicesMap: Record<string, Practice>;
  isLoading: boolean;
} => {
  const [practicesMap, setPracticesMap] = useState<
    Record<string, Practice>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!practiceIds.length) {
      setPracticesMap({});
      setIsLoading(false);
      return;
    }

    // Serve cached entries immediately
    const cached: Record<string, Practice> = {};
    const uncached: string[] = [];

    practiceIds.forEach((id) => {
      if (practiceCache.has(id)) {
        cached[id] = practiceCache.get(id)!;
      } else {
        uncached.push(id);
      }
    });

    if (Object.keys(cached).length > 0) {
      setPracticesMap((prev) => ({ ...prev, ...cached }));
    }

    if (uncached.length === 0) return;

    const batchKey = uncached.sort().join(',');
    // Snapshot the generation for each ID before the fetch so the .then
    // handler can detect whether an invalidation occurred while in flight.
    const capturedGens = Object.fromEntries(
      uncached.map((id) => [id, idGeneration.get(id) ?? 0]),
    );
    let fetchPromise = inFlightRequests.get(batchKey);
    setIsLoading(true);

    if (!fetchPromise) {
      fetchPromise = getPracticesList(
        null,
        { practiceIds: uncached },
        uncached.length + 1,
      )
        .then(({ data }) => {
          const practices = data?.practices || [];
          practices.forEach((p) => {
            // Skip the cache write if the ID was invalidated after this
            // fetch started — a fresher request is already in flight or
            // will be issued by the next mount.
            if ((idGeneration.get(p.id) ?? 0) === (capturedGens[p.id] ?? 0)) {
              practiceCache.set(p.id, p);
            }
          });
          // Guard: only evict our own entry so a later mount's fresh promise
          // is not removed if we resolve after it was installed.
          if (inFlightRequests.get(batchKey) === fetchPromise) {
            inFlightRequests.delete(batchKey);
          }
          return practices;
        })
        .catch((err) => {
          if (inFlightRequests.get(batchKey) === fetchPromise) {
            inFlightRequests.delete(batchKey);
          }
          throw err;
        });
      inFlightRequests.set(batchKey, fetchPromise);
    }

    fetchPromise
      .then((practices) => {
        if (!mountedRef.current) return;
        const result: Record<string, Practice> = {};
        practices.forEach((p) => {
          result[p.id] = p;
        });
        setPracticesMap((prev) => ({ ...prev, ...result }));
      })
      .catch(() => {})
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceIds.join(',')]);

  return { practicesMap, isLoading };
};
